import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LogoutIcon from '@mui/icons-material/Logout';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import NearMeRoundedIcon from '@mui/icons-material/NearMeRounded';

import Loading from '../components/Loading';
import { ErrorAlert } from '../components/Alerts';
import EntrySentCelebration from '../components/EntrySentCelebration';
import apiService from '../services/api';
import { Job, JobShift, JobPredictionObject, MyApplicationDetail } from '../types';
import { useAuth } from '../auth/AuthContext';
import { calcTotalPayment } from '../utils/salaryUtils';
import { isActiveApplicationStatus, applicationStatusLabel } from '../constants/applicationStatus';
import ImageViewer from '../components/ImageViewer';
import RankBadge from '../components/RankBadge';
import { STAGE_GRADIENT, resolveTierColor } from '../constants/rankColors';

interface JobParams {
  id: string;
  [key: string]: string | undefined;
}

// 日付フォーマット関数
const formatDate = (date: Date, format: string): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return format
    .replace('yyyy', year.toString())
    .replace('MM', month)
    .replace('dd', day);
};

// 募集締切日を計算
const calculateDeadline = (postedDate: string): string => {
  try {
    const date = new Date(postedDate);
    date.setDate(date.getDate() + 30);
    return formatDate(date, 'yyyy-MM-dd');
  } catch {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return formatDate(date, 'yyyy-MM-dd');
  }
};

// 期限切れ判定
const isShiftExpired = (shift: JobShift): boolean => {
  const shiftDate = new Date(shift.shift_date);
  const now = new Date();
  const jstFormatter = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const nowDateStr = jstFormatter.format(now);
  const shiftDateStr = jstFormatter.format(shiftDate);
  if (shiftDateStr < nowDateStr) return true;
  if (shiftDateStr === nowDateStr) {
    const jstTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const timeStr = jstTimeFormatter.format(now);
    const [currentHours, currentMinutes] = timeStr.split(':').map(Number);
    const [shiftHours, shiftMinutes] = shift.start_time.split(':').map(Number);
    if (shiftHours < currentHours || (shiftHours === currentHours && shiftMinutes < currentMinutes)) {
      return true;
    }
  }
  return false;
};

// 深夜跨ぎ判定（end < startならtrue）
const isMidnightCrossing = (startTime: string, endTime: string): boolean => {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return (eh * 60 + em) <= (sh * 60 + sm);
};

// 勤務時間を計算（例: "4.0"）
const calcHours = (shift: JobShift): string => {
  const [sh, sm] = shift.start_time.split(':').map(Number);
  const [eh, em] = shift.end_time.split(':').map(Number);
  let totalMinutes = (eh * 60 + em) - (sh * 60 + sm);
  if (totalMinutes <= 0) totalMinutes += 24 * 60; // 深夜跨ぎ対応
  const hours = totalMinutes / 60;
  return hours % 1 === 0 ? `${hours}.0` : hours.toFixed(1);
};

// 曜日の取得
const getDayOfWeek = (dateStr: string): string => {
  const date = new Date(dateStr);
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
  return daysOfWeek[date.getDay()];
};

// シフト日付フォーマット（カードヘッダー用: 2026.01.10(月)）
const formatShiftDateHeader = (shift: JobShift): string => {
  const date = new Date(shift.shift_date);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}(${getDayOfWeek(shift.shift_date)})`;
};

// シフト日付フォーマット（日時行用: 2026.01.10(日) 18:00-翩22:00）
const formatShiftDateDetail = (shift: JobShift): string => {
  const date = new Date(shift.shift_date);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const endLabel = isMidnightCrossing(shift.start_time, shift.end_time)
    ? `翌${shift.end_time}`
    : shift.end_time;
  return `${year}.${month}.${day}(${getDayOfWeek(shift.shift_date)}) ${shift.start_time}-${endLabel}`;
};

// 金額フォーマット
const formatAmount = (value: string | number | undefined): string => {
  if (!value) return '-';
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return `${num.toLocaleString()}円`;
};

// 実報酬合計の計算は src/utils/salaryUtils.ts の calcTotalPayment を使用

const JobDetail: React.FC = () => {
  const { id } = useParams<JobParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shiftIdFromQuery = queryParams.get('shift');

  const userHasApplied = queryParams.get('applied') === '1';
  const [userWorkCompleted, setUserWorkCompleted] = useState<boolean>(queryParams.get('completed') === '1');
  // 応募の状態。user_has_applied は「再応募できない」(=応募APIが409を返す)ことを表す旗で、
  // 不採用(rejected)・キャンセル(cancelled)・no_show も含む。出勤/勤務完了の表示は
  // 「進行中の応募」だけに出したいので、状態そのものを持って出し分ける。
  // undefined = まだ取得していない / null = 応募が無い。
  // 未取得のまま「進行中」と仮定すると、一覧から applied=1 付きで不採用の求人へ
  // 遷移したときに出勤ボタンと予測が一瞬出てから消える。
  const [applicationStatus, setApplicationStatus] = useState<string | null | undefined>(undefined);

  const [job, setJob] = useState<Job | null>(null);
  const [shift, setShift] = useState<JobShift | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [applySuccess, setApplySuccess] = useState<boolean>(userHasApplied);
  const [showEntrySentOverlay, setShowEntrySentOverlay] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const [, setPdfUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const { userId } = useAuth();
  const [appCompleted, setAppCompleted] = useState<boolean>(false);
  const [imageData, setImageData] = useState<Array<{page: number, data: string}> | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [laborTerms, setLaborTerms] = useState<{title: string, content: string}>({
    title: '労働条件通知書',
    content: '読み込み中...'
  });
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // ENTRY SENT 予測（予測報酬 / 予測獲得スキル。設計書 §6.3 / 付録D.2）
  const [prediction, setPrediction] = useState<JobPredictionObject | null>(null);
  // 予測 API は本人の store_user_id を必須 query で要求する（§7 自己所有・適格ティア算出）。
  const [primaryStoreUserId, setPrimaryStoreUserId] = useState<string | null>(null);
  const [userIsWorking, setUserIsWorking] = useState<boolean>(false);

  // 「進行中の応募がある」か。出勤/退勤ボタン・勤務完了バッジ・ENTRY SENT 予測は
  // これで出し分ける（不採用・キャンセル済みの応募には出さない）。
  // 未取得(undefined)のあいだだけ応募済みフラグで代替する（BEが未対応な場合や、
  // 応募直後でまだ再取得していない場合。応募直後は必ず進行中）。
  // 取得済みで null（応募が無い）なら false に落とす。
  const hasActiveApplication =
    applicationStatus === undefined
      ? (userHasApplied || applySuccess)
      : isActiveApplicationStatus(applicationStatus);

  const steps = ['労働条件確認', '応募確認'];

  const fetchLaborConditionsPDF = async () => {
    if (!id || !shiftIdFromQuery) return;
    setIsPdfLoading(true);
    setPdfError(null);
    try {
      const response = await apiService.getLaborConditionsPDF(id, shiftIdFromQuery, userId);
      if (response.success && response.data) {
        if (response.data.image_data) setImageData(response.data.image_data);
        if (response.data.pdf_url) setPdfUrl(response.data.pdf_url);
      } else {
        setPdfError('労働条件通知書の取得に失敗しました');
      }
    } catch {
      setPdfError('労働条件通知書の取得中にエラーが発生しました');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const laborResponse = await apiService.getLatestLaborConditionsTemplate();
      if (laborResponse.success && laborResponse.data && laborResponse.data.length > 0) {
        setLaborTerms({
          title: laborResponse.data[0].name || '労働条件通知書',
          content: laborResponse.data[0].content || '内容が取得できませんでした。'
        });
      }
    } catch {
      setError('契約書類の取得中にエラーが発生しました');
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    /**
     * シフト詳細と本人の出退勤状態（出勤中 / 勤務完了）を同期する。
     *
     * 勤務完了の判定は「このシフト」に限定しなければならない。以前は
     * 応募一覧を「日付 + 開始時刻」だけで突き合わせていたため、同日同時刻に
     * ある別店舗・別求人の勤務実績を拾ってしまい、まだ「OK!手伝う」を押して
     * いない求人に「勤務完了」バッジが出ていた（クルー向けマニュアル 2-2 No.5）。
     */
    const syncShiftAndAttendance = async (shiftId: string) => {
      const shiftResponse = await apiService.getShiftDetail(shiftId, userId);
      const shiftData = shiftResponse.success ? shiftResponse.data : null;
      if (shiftData) {
        setShift(shiftData);
        if (shiftData.user_has_applied) {
          setApplySuccess(true);
        }
        // 常に代入する（応募が取り消された等で状態が消えたら null に戻す）。
        // BEが未対応でフィールドが無い場合も null になり、下の応募済みフラグに
        // フォールバックするため従来挙動のまま。
        setApplicationStatus(shiftData.user_application_status ?? null);
        if (shiftData.user_work_completed) {
          setUserWorkCompleted(true);
        }
      }
      if (!userId) return;

      const today = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const appRes = await apiService.getMyApplicationsWithDetails(userId, today);
      if (!appRes.success || !appRes.data) return;

      // 突き合わせは shift_id の一致のみ。/applications/with-details は
      // JobApplication.shift_id = JobShift.id の INNER JOIN なので shift_id は必ず入る。
      const app = appRes.data.find((a: MyApplicationDetail) => a.shift_id === shiftId);
      // app が見つからないときは据え置く。この API は shift_date >= 今日(JST) で絞るため、
      // 日付を跨いだ夜勤では勤務中の応募がレスポンスから消える。無条件に false を書くと
      // 「退勤する」が「出勤する」に戻ってしまう。
      if (app) {
        setUserIsWorking(!!(app.actual_check_in_time && !app.actual_check_out_time));
        if (app.actual_check_in_time && app.actual_check_out_time) {
          setUserWorkCompleted(true);
        }
      }
    };

    const fetchJobDetail = async () => {
      setLoading(true);
      try {
        if (!id) {
          setError('求人IDが見つかりません');
          setLoading(false);
          return;
        }
        const jobResponse = await apiService.getJob(id);
        if (!jobResponse.success || !jobResponse.data) {
          setError('求人情報の取得に失敗しました');
          setLoading(false);
          return;
        }
        const jobData = jobResponse.data;
        const formattedJob: Job = {
          id: jobData.id.toString(),
          title: jobData.job_title || '',
          department: jobData.job_type || '',
          description: jobData.content || jobData.description || '',
          requirements: [],
          location: jobData.location || '',
          store_name: jobData.store_name || '',
          salary_type: jobData.salary_type || '',
          salary_allowance: jobData.salary_allowance,
          salary_amount: jobData.salary_amount,
          salary_transport: jobData.salary_transport,
          postedDate: jobData.created_at ? formatDate(new Date(jobData.created_at), 'yyyy-MM-dd') : '',
          deadline: jobData.created_at ? calculateDeadline(jobData.created_at) : '',
          status: jobData.is_active ? 'open' : 'closed',
          job_title: jobData.job_title || '',
          job_type: jobData.job_type || '',
          content: jobData.content || '',
          is_active: jobData.is_active,
          created_at: jobData.created_at,
          updated_at: jobData.updated_at
        };
        setJob(formattedJob);
        if (shiftIdFromQuery) {
          await syncShiftAndAttendance(shiftIdFromQuery);
        }
      } catch {
        setError('情報の取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetail();

    let intervalId: NodeJS.Timeout;
    if (shiftIdFromQuery) {
      intervalId = setInterval(async () => {
        try {
          await syncShiftAndAttendance(shiftIdFromQuery);
        } catch {
          // バックグラウンド更新エラーは無視
        }
      }, 5 * 60 * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // userId は応募一覧の突き合わせに必要。認証が後から解決されるケースがあるため依存に含める。
  }, [id, shiftIdFromQuery, userId]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 予測 API 用に本人の primary StoreUser.id を解決する（§7 自己所有・適格ティア算出）。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) {
        setPrimaryStoreUserId(null);
        return;
      }
      try {
        const res = await apiService.getStoreUser(userId);
        if (!cancelled && res.success && res.data && res.data.length > 0) {
          setPrimaryStoreUserId(res.data[0].id);
        }
      } catch {
        // 予測は付加情報のため失敗は黙殺（本体フローに影響させない）
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ENTRY SENT 状態（進行中の応募あり）になったら予測報酬 / 予測獲得スキルを取得
  useEffect(() => {
    if (!hasActiveApplication || !shiftIdFromQuery || !primaryStoreUserId) {
      setPrediction(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getJobPrediction(shiftIdFromQuery, primaryStoreUserId);
        if (!cancelled && res.success && res.data) {
          setPrediction(res.data);
        }
      } catch {
        // 予測は付加情報のため失敗は黙殺（本体フローに影響させない）
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasActiveApplication, shiftIdFromQuery, primaryStoreUserId]);

  const handleGoBack = () => {
    // LINEの募集案件リンクなどから直接遷移してきた場合は履歴がないため、
    // ホーム（求人一覧）に遷移する
    if (location.key === 'default') {
      navigate('/jobs');
    } else {
      navigate(-1);
    }
  };

  const handleOpenApplyDialog = async () => {
    if (shiftIdFromQuery) {
      try {
        const shiftResponse = await apiService.getShiftDetail(shiftIdFromQuery);
        if (shiftResponse.success && shiftResponse.data) {
          const updatedShift = shiftResponse.data;
          setShift(updatedShift);
          if (isShiftExpired(updatedShift) || updatedShift.is_full) return;
        }
      } catch {
        setError('シフト情報の更新中にエラーが発生しました');
        return;
      }
    }
    setActiveStep(0);
    setTermsAccepted(false);
    setAppCompleted(false);
    setIsApplying(false);
    setIsDialogOpen(true);
    fetchDocuments();
    if (id && shiftIdFromQuery) fetchLaborConditionsPDF();
  };

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleApplyWithTerms = async (acceptedTerms: boolean) => {
    if (!shift) return;
    try {
      const userResponse = await apiService.getUser(userId);
      if (!userResponse.success || !userResponse.data) {
        setError('求人情報の取得に失敗しました');
        setLoading(false);
        return;
      }
      const user = userResponse.data;

      // 姓・名が両方登録されていない場合はマイページでの登録を促す
      if (!user.last_name?.trim() || !user.first_name?.trim()) {
        setError('応募にはお名前（姓・名）の登録が必要です。マイページの「プロフィール変更」から登録してください。');
        setIsApplying(false);
        setIsDialogOpen(false);
        return;
      }

      const response = await apiService.applyForShift(shift.id, {
        userId: userId ?? '',
        last_name: user.last_name,
        first_name: user.first_name,
        email: user?.email,
        phone: user?.phone || '',
        agreedToLaborConditions: acceptedTerms
      });
      if (response.success) {
        setApplySuccess(true);
        setIsDialogOpen(false);
        setShowEntrySentOverlay(true);
        if (response.data && response.data.application_id) {
          await apiService.saveLaborConditionsAgreement(
            job?.id || '',
            shift.id.toString(),
            response.data.application_id,
            {
              userId: userId,
              name: user?.name || '応募者',
              email: user?.email || '',
              phone: user?.phone || ''
            }
          );
        }
        if (response.data) {
          setShift({
            ...shift,
            current_applicants: response.data.current_applicants,
            is_full: response.data.is_full
          });
        }
      } else {
        const rawError = response.error || '応募に失敗しました';
        const normalized = rawError.toLowerCase();
        if (normalized.includes('already applied')) {
          setError('既に応募が完了しています！当日お待ちしています！');
        } else {
          setError(rawError);
        }
      }
    } catch {
      setError('応募処理中にエラーが発生しました');
    } finally {
      setIsApplying(false);
    }
  };

  // 応募ボタンコンテンツ
  const getApplyButtonContent = () => {
    if (!shift) return { text: 'OK 手伝う!', disabled: false, color: 'primary' as const };
    const expired = isShiftExpired(shift);
    if (isApplying) return { text: '応募中...', disabled: true, color: 'primary' as const };
    // 応募APIは status を問わず 409 を返すので、応募がある限り常に無効。
    // ただし文言は状態で出し分ける（不採用なのに「応募済み」だと理由が分からない）。
    if (userHasApplied || applySuccess) {
      return {
        text: applicationStatusLabel(applicationStatus),
        disabled: true,
        color: 'primary' as const,
      };
    }
    if (expired) return { text: '募集終了', disabled: true, color: 'error' as const };
    if (shift.is_full) return { text: '定員に達しました', disabled: true, color: 'error' as const };
    return { text: 'OK 手伝う!', disabled: false, color: 'primary' as const };
  };

  // 応募確認ダイアログ（関数コンポーネントではなくJSX変数として定義し、remountを防止）
  const applyConfirmDialog = (
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: '420px' }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography variant="h6" sx={{ mb: 2 }}>
            ステップ {activeStep + 1}: {steps[activeStep]}
          </Typography>

          {activeStep === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>{laborTerms.title}</Typography>
              {isPdfLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : pdfError ? (
                <Alert severity="error" sx={{ my: 2 }}>{pdfError}</Alert>
              ) : imageData ? (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <ImageViewer
                    imageData={imageData}
                    width={windowWidth > 600 ? 600 : windowWidth - 40}
                    height={500}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>労働条件通知書を読み込み中...</Typography>
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={`上記${laborTerms.title}の内容に同意します`}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {activeStep === 1 && shift && (
            <Box>
              <Typography variant="h6" gutterBottom>応募内容の確認</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">シフト詳細</Typography>
                <Typography variant="body2" gutterBottom>
                  日付: {`${new Date(shift.shift_date).getFullYear()}年${new Date(shift.shift_date).getMonth() + 1}月${new Date(shift.shift_date).getDate()}日（${getDayOfWeek(shift.shift_date)}）`}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  時間: {shift.start_time} 〜 {shift.end_time}
                </Typography>
                <Typography variant="body2" gutterBottom>場所: {shift.location}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">同意事項</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CheckBoxIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                  <Typography variant="body2">{laborTerms.title}に同意</Typography>
                </Box>
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack}>戻る</Button>
          )}
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={activeStep === 0 && !termsAccepted}
            >
              次へ
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (!shift) return;
                setIsApplying(true);
                handleApplyWithTerms(termsAccepted);
              }}
              color="primary"
              variant="contained"
              disabled={isApplying}
            >
              {isApplying ? (
                <><CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />応募中...</>
              ) : '応募する'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
  );

  if (loading) return <Loading message="求人情報を読み込み中..." />;

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <ErrorAlert message={error} onClose={() => setError(null)} />
        <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} sx={{ mt: 2 }}>
          求人一覧に戻る
        </Button>
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>求人情報が見つかりませんでした</Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleGoBack} sx={{ mt: 2 }}>
          求人一覧に戻る
        </Button>
      </Box>
    );
  }

  const applyButton = getApplyButtonContent();
  const expired = shift ? isShiftExpired(shift) : false;
  const fillRate = shift ? Math.min((shift.current_applicants / shift.required_workers) * 100, 100) : 0;
  const remaining = shift ? shift.required_workers - shift.current_applicants : 0;
  const isRecruiting = !expired && !(shift?.is_full);

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 3 }}>
      {/* ENTRY SENT 演出（応募成功直後に1回だけ表示） */}
      <EntrySentCelebration
        open={showEntrySentOverlay}
        onClose={() => {
          setShowEntrySentOverlay(false);
          navigate('/jobs');
        }}
        jobTitle={job?.title}
        prediction={prediction}
      />

      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 1,
        }}
      >
        <SearchIcon sx={{ ml: 0.5, color: 'text.secondary', fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold', fontSize: '1rem' }}>
          求人を探す
        </Typography>
      </Box>

      {/* 店舗情報 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 2,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            mr: 2,
            bgcolor: '#9e9e9e',
            flexShrink: 0,
            borderRadius: '50%',
          }}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
            {job.store_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {job.location}
          </Typography>
        </Box>
      </Box>

      {/* メインカード */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: '2px solid #d9f1f0',
            borderLeft: '2px solid #00a29a',
            boxShadow: 'none',
          }}
        >
          {/* 日付 + 募集中チップ */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ fontSize: 13, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {shift ? formatShiftDateHeader(shift) : job.postedDate}
              </Typography>
            </Box>
            {isRecruiting ? (
              <Chip
                label="募集中"
                size="small"
                sx={{
                  bgcolor: '#d0f3ee',
                  color: '#13c3a8',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            ) : (
              <Chip
                label={expired ? '募集終了' : '満員'}
                size="small"
                color="error"
                sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>

          {/* 店舗名（カード内小字） */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {job.store_name}
          </Typography>

          {/* 求人タイトル */}
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mb: 1.5, fontSize: '1.1rem', lineHeight: 1.4 }}
          >
            {job.title}
          </Typography>

          {/* 求人状況 + プログレスバー */}
          {shift && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">求人状況</Typography>
                <Typography variant="body2" color="text.secondary">
                  {shift.is_full ? '満員' : `残り${remaining}名`}
                </Typography>
              </Box>
              {/* グラデーション進捗バー */}
              <Box
                sx={{
                  position: 'relative',
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e0e0',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${shift.is_full ? 100 : fillRate}%`,
                    borderRadius: 4,
                    background: 'linear-gradient(to right, #f5a623, #f07020, #e03010)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
            </Box>
          )}

          {/* 詳細テーブル */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden',
              mb: 2,
            }}
          >
            {/* 日時行（シフトがある場合のみ） */}
            {shift && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1.2,
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ width: 52, flexShrink: 0, fontWeight: 'bold' }}
                >
                  日時
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>
                  {formatShiftDateDetail(shift)}
                </Typography>
                <Chip
                  label={`${calcHours(shift)}h`}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5',
                    fontSize: '0.75rem',
                    height: 22,
                    ml: 1,
                    flexShrink: 0,
                  }}
                />
              </Box>
            )}

            {/* 時給行 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1.2,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="body2"
                sx={{ width: 52, flexShrink: 0, fontWeight: 'bold' }}
              >
                {(shift?.salary_type || job.salary_type) === 'daily' ? '日給' : (shift?.salary_type || job.salary_type) === 'monthly' ? '月給' : '時給'}
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'right' }}>
                {formatAmount(shift?.salary_amount ?? job.salary_amount)}
              </Typography>
            </Box>

            {/* 手当行 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1.2,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="body2"
                sx={{ width: 52, flexShrink: 0, fontWeight: 'bold' }}
              >
                手当て
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'right' }}>
                {formatAmount(shift?.salary_allowance ?? job.salary_allowance)}
              </Typography>
            </Box>

            {/* 交通費行 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1.2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ width: 52, flexShrink: 0, fontWeight: 'bold' }}
              >
                交通費
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'right' }}>
                {formatAmount(shift?.salary_transport ?? job.salary_transport)}
              </Typography>
            </Box>

            {/* 合計報酬行（シフトが選択されている場合のみ） */}
            {shift && calcTotalPayment(
              shift.salary_amount,
              shift.salary_type,
              shift.start_time,
              shift.end_time,
              shift.break_duration_minutes ?? 0,
              shift.salary_allowance,
              shift.salary_transport
            ) !== null && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1.2,
                  bgcolor: '#fff8e1',
                  borderTop: '2px solid #f5a623',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ width: 52, flexShrink: 0, fontWeight: 'bold', color: '#e65100' }}
                >
                  合計
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ flex: 1, textAlign: 'right', color: '#e65100' }}>
                  ¥{calcTotalPayment(
                    shift.salary_amount,
                    shift.salary_type,
                    shift.start_time,
                    shift.end_time,
                    shift.break_duration_minutes ?? 0,
                    shift.salary_allowance,
                    shift.salary_transport
                  )!.toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>

          {/* 仕事内容 */}
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            仕事内容
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
            {job.description}
          </Typography>

          {/* ENTRY SENT: 予測報酬 / 予測獲得スキル（応募済み時のみ。設計書 §6.3 / 付録D.2） */}
          {hasActiveApplication && prediction && (
            <Box
              sx={{
                mb: 2,
                borderRadius: 2,
                p: 2,
                color: '#fff',
                // 付録C.5: 上側 青紫 → 下側 マゼンタ紫（左下→右上の対角）
                background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <NearMeRoundedIcon sx={{ fontSize: 20, color: STAGE_GRADIENT.accentTo }} />
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', letterSpacing: 1 }}>ENTRY SENT!</Typography>
                <Chip
                  label="予測"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '0.72rem', opacity: 0.9, mb: 1.5 }}>
                店長に通知を送りました！
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.95, mb: 0.75 }}>
                この仕事で得られるバリュー
              </Typography>

              {/* 予測報酬（実支払予測の総額。min !== max の場合のみレンジ表示） */}
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.12)',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1.2,
                  mb: prediction.predicted_skills.length > 0 ? 1.5 : 0,
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', opacity: 0.85, mb: 0.3 }}>予測報酬</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.25rem', lineHeight: 1.2 }}>
                  {prediction.predicted_reward_min === prediction.predicted_reward_max
                    ? `¥${prediction.predicted_reward_max.toLocaleString()}`
                    : `¥${prediction.predicted_reward_min.toLocaleString()} 〜 ¥${prediction.predicted_reward_max.toLocaleString()}`}
                </Typography>
                {prediction.required_tier && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', opacity: 0.85 }}>必要ランク</Typography>
                    <RankBadge tier={prediction.required_tier} size={16} labelFontSize="0.7rem" />
                  </Box>
                )}
              </Box>

              {/* 予測獲得スキル chips */}
              {prediction.predicted_skills.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', opacity: 0.85, mb: 0.6 }}>予測獲得スキル</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {prediction.predicted_skills.map((sk, idx) => (
                      <Chip
                        key={`${sk.name}-${idx}`}
                        label={sk.name}
                        size="small"
                        sx={{
                          bgcolor: '#fff',
                          color: resolveTierColor(sk.tier?.code, sk.tier?.color),
                          fontWeight: 'bold',
                          fontSize: '0.72rem',
                          height: 24,
                          border: `1px solid ${resolveTierColor(sk.tier?.code, sk.tier?.color)}`,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography sx={{ fontSize: '0.62rem', opacity: 0.7, mt: 1.2 }}>
                ※実績や評価により変動する予測値です
              </Typography>
            </Box>
          )}

          {/* ボタン行 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* 応募していない求人／終端状態の応募に勤務完了は出さない */}
            {userWorkCompleted && hasActiveApplication && (
              <Chip
                label="勤務完了"
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#e3f2fd',
                  color: '#1565c0',
                  fontWeight: 'bold',
                }}
              />
            )}
            {hasActiveApplication && (
              <Button
                fullWidth
                variant="contained"
                disabled={userWorkCompleted}
                onClick={() => navigate('/attendance', { state: { from: 'jobDetail' } })}
                sx={{
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  bgcolor: userWorkCompleted ? '#cfd8dc' : userIsWorking ? '#c62828' : '#2e7d32',
                  color: userWorkCompleted ? '#607d8b' : '#fff',
                  py: 1.2,
                  '&:hover': { bgcolor: userWorkCompleted ? '#cfd8dc' : userIsWorking ? '#b71c1c' : '#1b5e20' },
                }}
                startIcon={userIsWorking ? <LogoutIcon /> : <HowToRegIcon />}
              >
                {userWorkCompleted ? '勤務完了' : userIsWorking ? '退勤する' : '出勤する'}
              </Button>
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleGoBack}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  bgcolor: '#c4c4c4',
                  color: '#3c3c3c',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#b0b0b0', boxShadow: 'none' },
                }}
              >
                戻る
              </Button>
              <Button
                variant="contained"
                disabled={applyButton.disabled}
                onClick={applyButton.disabled ? undefined : handleOpenApplyDialog}
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  bgcolor: applyButton.disabled ? undefined : '#0071e3',
                  color: '#fff',
                  '&:hover': { bgcolor: applyButton.disabled ? undefined : '#005bb5' },
                }}
                startIcon={
                  applySuccess ? <HowToRegIcon /> :
                  (expired || shift?.is_full) ? <BlockIcon /> :
                  undefined
                }
              >
                {isApplying ? (
                  <><CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />応募中...</>
                ) : applyButton.text}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* 応募確認ダイアログ */}
      {applyConfirmDialog}
    </Box>
  );
};

export default JobDetail;
